import type { ReactNode } from 'react';

import { DataType } from '@revelio/core';

import type { LocationType } from '../Chart/Map/types';
export { DataType };

export type SeriesType = 'primary' | 'benchmark' | 'other';

export type SingleDataPoint<T extends object = object> = T & {
  dim: string;
  longName?: string | null;
  value: number | null;
  share?: number | null;
  id?: string | null;
  drilldownId?: string | null;
  seriesType?: SeriesType;
  color?: string;
};
export type SingleDataSet<T extends object = object> = {
  type: DataType.SINGLE;
  data: SingleDataPoint<T>[];
};

export type TimeseriesDataPoint = {
  date: string;
  value: number | null;
  share?: number | null;
  secondaryValue?: number | null;
};

export type TimeseriesData = {
  id?: string | null;
  dim: string;
  timeseries: TimeseriesDataPoint[];
};

export type TimeseriesDataSet = {
  type: DataType.TIMESERIES;
  data: TimeseriesData[];
};

export type BoxDataPoint = {
  q10: number;
  q25?: number; // Q1 quartile – populated when histogram data is available
  q50: number;
  q75?: number; // Q3 quartile – populated when histogram data is available
  q90: number;
};

export type PercentileDataPoint = {
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
};

export type BoxDataSet = {
  dim: string;
  id?: string | null;
  box: BoxDataPoint;
};

/**
 * Box plot dataset (non-breakdown).
 * Note: this dataset type is currently not produced by any CustomDash DataProvider,
 * but it is part of the shared DataType enum and may appear in future providers.
 */
export type BoxSeriesDataSet = {
  type: DataType.BOX;
  data: BoxDataSet[];
};

export type BreakdownData<
  T extends SingleDataPoint | TimeseriesDataPoint | BoxDataSet,
> = {
  dim: string;
  id?: string | null;
  breakdown: T[];
};

export type TransitionBreakdownData = BreakdownData<SingleDataPoint> & {
  inflowBreakdown?: SingleDataPoint[];
  outflowBreakdown?: SingleDataPoint[];
};

export type BreakdownSingleDataSet = {
  type: DataType.BREAKDOWN_SINGLE;
  data: BreakdownData<SingleDataPoint>[];
};

export type FlowBreakdownSingleDataSet = {
  type: DataType.FLOW_BREAKDOWN_SINGLE;
  data: TransitionBreakdownData[];
};

export type BreakdownTimeseriesDataSet = {
  type: DataType.BREAKDOWN_TIMESERIES;
  data: BreakdownData<TimeseriesDataPoint>[];
};

/** Box plot with a secondary breakdown; uses {@link DataType.BOX} (discriminate with {@link isBoxBreakdownDataSet}). */
export type BreakdownBoxDataSet = {
  type: DataType.BOX;
  data: BreakdownData<BoxDataSet>[];
};

export type DistributionDataPoint = {
  value: number;
  kde: number;
  cdf: number;
};

export type DistributionData = {
  dim: string;
  id?: string | null;
  distribution: DistributionDataPoint[];
  percentiles?: Pick<PercentileDataPoint, 'p25' | 'p50' | 'p75'>;
};

export type DistributionDataSet = {
  type: DataType.DISTRIBUTION;
  data: DistributionData[];
};

export type MapDataSet = {
  type: DataType.MAP;
  points: Array<{
    id: string;
    label: string;
    lat: number;
    lon: number;
    value: number;
    tooltip?: Record<string, number | string>;
    locationType: LocationType;
  }>;
};

export type TableRowsDataSet = {
  type: DataType.TABLE_ROWS;
  data: Array<Record<string, unknown>>;
};

export type TextDataSet = {
  type: DataType.TEXT;
  data: Array<{
    entity: string;
    id?: string | null;
    text: string;
    metadata?: Record<string, unknown>;
  }>;
};

export type DataProviderSet =
  | SingleDataSet
  | TimeseriesDataSet
  | BreakdownSingleDataSet
  | FlowBreakdownSingleDataSet
  | BreakdownTimeseriesDataSet
  | BreakdownBoxDataSet
  | BoxSeriesDataSet
  | DistributionDataSet
  | MapDataSet
  | TableRowsDataSet
  | TextDataSet;

export enum DataProviderQueryStatus {
  Idle = 'idle',
  Fetching = 'fetching',
  Error = 'error',
  Success = 'success',
}

export interface DataProviderResult<T = unknown> {
  data: T | undefined;
  status: DataProviderQueryStatus;
  error?: Error;
  haveSelectionsBeenModified?: boolean;
  /** Month labels derived from request filters when the API omits `refs.months` (Composition V2.1). */
  monthRefs?: string[];
}

export type DataProviderChildren<T = unknown> = (
  props: DataProviderResult<T>
) => ReactNode;

export const isBreakdownTimeseriesData = (
  data: DataProviderSet
): data is BreakdownTimeseriesDataSet => {
  return data.type === DataType.BREAKDOWN_TIMESERIES;
};

export const isSingleDataSet = (data: DataProviderSet): data is SingleDataSet =>
  data.type === DataType.SINGLE;

export const isBoxBreakdownDataSet = (
  data: DataProviderSet
): data is BreakdownBoxDataSet => {
  if (data.type !== DataType.BOX) return false;
  const first = data.data[0];
  return first !== undefined && 'breakdown' in first;
};

export const isBoxDataPoint = (value: unknown): value is BoxDataPoint => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as BoxDataPoint).q10 === 'number' &&
    typeof (value as BoxDataPoint).q50 === 'number' &&
    typeof (value as BoxDataPoint).q90 === 'number'
  );
};

export const isPercentileDataPoint = (
  value: unknown
): value is PercentileDataPoint => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as PercentileDataPoint;
  return (
    typeof v.p10 === 'number' &&
    typeof v.p25 === 'number' &&
    typeof v.p50 === 'number' &&
    typeof v.p75 === 'number' &&
    typeof v.p90 === 'number'
  );
};
